import React, { useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, Info, LogOut, UserCircle, BarChart3, Globe, Settings, Users, Building2, Lock, ArrowLeftRight, Shield, BookOpen } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { getUserAvatar, getUserInitials } from "../../utils/userAvatar";
import { buildHomeNavigationTarget, resolveActiveSiteCode } from "../../utils/navigationContext";
import ThemeToggle from "../common/ThemeToggle";

interface UserMenuProps {
  displayName: string;
  displayRole: string;
  site?: string;
  menuOpen: boolean;
  onToggleMenu: () => void;
  onSiteInfo: () => void;
  onLogout: () => void;
  showSiteNavigation?: boolean;
}

const UserMenu: React.FC<UserMenuProps> = ({
  displayName,
  displayRole,
  site,
  menuOpen,
  onToggleMenu,
  onSiteInfo,
  onLogout,
  showSiteNavigation,
}) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isSelectionPage = location.pathname.startsWith('/select-company') || location.pathname.startsWith('/select-site');
  const canShowSiteNav = showSiteNavigation !== undefined ? showSiteNavigation : !isSelectionPage;

  const { user, isGlobalAdmin } = useAuth();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [buttonPosition, setButtonPosition] = React.useState({ top: 0, right: 0 });
  const [userAvatar, setUserAvatar] = React.useState<string | null>(null);

  // Check if user is admin - Updated to include new role system
  const roleName = String((user as any)?.roleName || user?.role || '').toLowerCase();
  const isAdmin =
    user?.role === 'admin' ||
    isGlobalAdmin ||
    (user as any)?.is_superuser ||
    (user as any)?.is_super_admin ||
    ['system_admin', 'super_admin', 'ho_admin', 'company_admin', 'site_admin'].includes(roleName);

  // HO Admin or Site Admin (non-super) can switch sites within their company
  const isHoAdmin = roleName === 'ho_admin' || roleName === 'company_admin' || (roleName === 'admin' && !isGlobalAdmin && !(user as any)?.is_superuser && !user?.siteId);
  const isSiteAdmin = roleName === 'site_admin' || (roleName === 'admin' && !isGlobalAdmin && !(user as any)?.is_superuser && !!user?.siteId);
  const canSwitchSite = isHoAdmin || isGlobalAdmin;

  // Load avatar from user, localStorage, or AuthContext
  useEffect(() => {
    const loadAvatar = () => {
      // Try multiple sources: user object, localStorage, and AuthContext
      // Include user identifier (username/id) so avatar is user-specific
      const avatarSources = {
        avatar: user?.avatar,
        profile_picture: (user as any)?.profile_picture || user?.avatar,
        first_name: user?.fullName?.split(' ')[0],
        last_name: user?.fullName?.split(' ').slice(1).join(' '),
        username: user?.username,
        id: (user as any)?.id,
        user_id: (user as any)?.user_id
      };

      const avatar = getUserAvatar(avatarSources);
      setUserAvatar(avatar);
    };

    loadAvatar();

    // Listen for avatar updates from storage and custom events
    const handleStorageChange = () => {
      loadAvatar();
    };

    const handleAvatarUpdate = () => {
      loadAvatar();
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('avatarUpdated', handleAvatarUpdate);
    // Also check periodically for updates within the same tab
    const interval = setInterval(loadAvatar, 1000);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('avatarUpdated', handleAvatarUpdate);
      clearInterval(interval);
    };
  }, [user]);

  const getInitials = () => {
    return getUserInitials({
      first_name: displayName?.split(' ')[0],
      last_name: displayName?.split(' ').slice(1).join(' '),
      username: user?.username || displayName
    });
  };

  useEffect(() => {
    if (menuOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setButtonPosition({
        top: rect.bottom + 8,
        right: window.innerWidth - rect.right
      });
    }
  }, [menuOpen]);

  const handleNavigationClick = () => {
    onToggleMenu();
    const resolvedSiteCode = resolveActiveSiteCode({
      propSite: site,
      userSiteCode: user?.siteCode,
      userTenant: user?.tenant,
      preferStoredContext: true
    });
    if (resolvedSiteCode) {
      navigate(`/navigate/${resolvedSiteCode}`);
    } else {
      navigate(
        buildHomeNavigationTarget({
          user,
          isGlobalAdmin,
          siteCode: resolvedSiteCode
        })
      );
    }
  };

  const handleDashboardClick = () => {
    onToggleMenu();
    const resolvedSiteCode = resolveActiveSiteCode({
      propSite: site,
      userSiteCode: user?.siteCode,
      userTenant: user?.tenant,
      preferStoredContext: true
    });
    if (resolvedSiteCode) {
      navigate(`/dashboard/${resolvedSiteCode}`);
    } else {
      navigate(
        buildHomeNavigationTarget({
          user,
          isGlobalAdmin,
          siteCode: resolvedSiteCode
        })
      );
    }
  };

  const handleProfileClick = () => {
    onToggleMenu();
    const resolvedSiteCode = resolveActiveSiteCode({
      propSite: site,
      userSiteCode: user?.siteCode,
      userTenant: user?.tenant,
      preferStoredContext: true
    });
    if (resolvedSiteCode) {
      navigate(`/profile/${resolvedSiteCode}`);
    } else {
      navigate('/profile');
    }
  };

  const handleAdminDashboardClick = () => {
    onToggleMenu();
    navigate('/admin');
  };

  const handleAdminUsersClick = () => {
    onToggleMenu();
    navigate('/admin/users');
  };

  const handleAdminSitesClick = () => {
    onToggleMenu();
    navigate('/admin/sites');
  };

  const handleAdminSecurityClick = () => {
    onToggleMenu();
    navigate('/admin/security');
  };

  const handleSwitchCompanyClick = () => {
    onToggleMenu();
    navigate('/select-company');
  };

  const handleSwitchSiteClick = () => {
    onToggleMenu();
    const companyId = user?.companyId || sessionStorage.getItem('company_id') || localStorage.getItem('company_id');
    if (companyId) {
      navigate(`/select-site?company_id=${companyId}`);
    } else {
      navigate('/select-site');
    }
  };

  return (
    <div
      className="relative"
      style={{ isolation: 'isolate' }}
    >
      <button
        ref={buttonRef}
        className="glass-button flex items-center justify-center p-0 rounded-xl hover-lift w-10 h-10 transition-all duration-300 overflow-hidden"
        title={displayName}
        type="button"
        onClick={onToggleMenu}
      >
        <div className="relative w-full h-full">
          {userAvatar ? (
            <img
              src={userAvatar}
              alt={displayName}
              className="w-full h-full object-cover"
              onError={(e) => {
                // Fallback to initials if image fails
                e.currentTarget.style.display = 'none';
                const parent = e.currentTarget.parentElement;
                if (parent) {
                  const fallback = document.createElement('div');
                  fallback.className = 'profile-initials w-full h-full bg-gradient-to-br from-[#0084CF] to-[#0066A3] flex items-center justify-center text-white text-xs';
                  fallback.textContent = getInitials();
                  parent.appendChild(fallback);
                }
              }}
            />
          ) : (
            <div className="profile-initials w-full h-full bg-gradient-to-br from-[#0084CF] to-[#0066A3] flex items-center justify-center text-white text-xs">
              {getInitials()}
            </div>
          )}
          <div className="absolute -bottom-0.5 -right-0.5 w-2.5 h-2.5 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
        </div>
      </button>

      {menuOpen && createPortal(
        <div
          ref={menuRef}
          className="user-dropdown-menu"
          style={{
            position: 'fixed',
            top: `${buttonPosition.top}px`,
            right: `${buttonPosition.right}px`,
            width: '256px',
            maxHeight: 'calc(100vh - 120px)',
            zIndex: 99999,
            pointerEvents: 'auto'
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="px-4 py-3 dropdown-header">
            <div className="flex items-center gap-3">
              <div className="relative flex-shrink-0">
                {userAvatar ? (
                  <img
                    src={userAvatar}
                    alt={displayName}
                    className="w-12 h-12 rounded-xl object-cover border-2 border-[#C2DFF1]"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const parent = e.currentTarget.parentElement;
                      if (parent) {
                        const fallback = document.createElement('div');
                        fallback.className = 'profile-initials w-12 h-12 bg-gradient-to-br from-[#0084CF] to-[#0066A3] rounded-xl flex items-center justify-center text-white text-sm border-2 border-[#C2DFF1]';
                        fallback.textContent = getInitials();
                        parent.appendChild(fallback);
                      }
                    }}
                  />
                ) : (
                  <div className="profile-initials w-12 h-12 bg-gradient-to-br from-[#0084CF] to-[#0066A3] rounded-xl flex items-center justify-center text-white text-sm border-2 border-[#C2DFF1]">
                    {getInitials()}
                  </div>
                )}
                <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 bg-emerald-400 rounded-full border-2 border-white shadow-sm" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm axon-user-name truncate">
                  {displayName}
                </div>
                <div className="text-xs axon-user-role capitalize">
                  {displayRole}
                </div>
                {user?.email && (
                  <div className="text-xs mt-0.5 axon-user-email truncate">
                    {user.email}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="py-1 overflow-y-auto max-h-[calc(100vh-180px)]">
            {/* Quick Actions - Only shown when not on selection pages */}
            {canShowSiteNav && (
              <>
                <div className="px-4 py-1">
                  <div className="text-[10px] uppercase tracking-wider axon-menu-label">Quick Actions</div>
                </div>
                <button
                  onClick={handleNavigationClick}
                  className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
                >
                  <Globe size={16} className="text-gray-400" />
                  <span className="header-widget-bold">Site Navigation</span>
                </button>
              </>
            )}



            {/* Admin Section - Only visible to admins */}
            {isAdmin && (
              <>
                <div className="my-1 mx-4 border-t border-[#C2DFF1]/50" />
                <div className="px-4 py-1">
                  <div className="text-[10px] uppercase tracking-wider axon-menu-label">Admin Panel</div>
                </div>

                <button
                  onClick={handleAdminDashboardClick}
                  className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
                >
                  <Shield size={16} className="text-gray-400" />
                  <span className="admin-dashboard-text">Admin Dashboard</span>
                </button>

                <button
                  onClick={handleAdminUsersClick}
                  className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
                >
                  <Users size={16} className="text-gray-400" />
                  <span className="header-widget-bold">User Management</span>
                </button>

                {/* Site Management - Super Admin & HO Admin only */}
                {(isGlobalAdmin || isHoAdmin) && (
                  <button
                    onClick={handleAdminSitesClick}
                    className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
                  >
                    <Building2 size={16} className="text-gray-400" />
                    <span className="header-widget-bold">Site Management</span>
                  </button>
                )}

                {/* Security & Monitoring - Super Admin & HO Admin only */}
                {(isGlobalAdmin || isHoAdmin) && (
                  <button
                    onClick={handleAdminSecurityClick}
                    className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
                  >
                    <Lock size={16} className="text-gray-400" />
                    <span className="header-widget-bold">Security & Monitoring</span>
                  </button>
                )}

                {isGlobalAdmin && (
                  <button
                    onClick={handleSwitchCompanyClick}
                    className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-[#1C4D88] hover:bg-[#F2F8FC]"
                  >
                    <Building2 size={16} className="text-[#0084CF]" />
                    <span className="header-widget-bold">Switch Company</span>
                  </button>
                )}

                {canSwitchSite && (
                  <button
                    onClick={handleSwitchSiteClick}
                    className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-[#1C4D88] hover:bg-[#F2F8FC]"
                  >
                    <ArrowLeftRight size={16} className="text-[#0084CF]" />
                    <span className="header-widget-bold">Switch Site</span>
                  </button>
                )}
              </>
            )}

            <div className="my-1 mx-4 border-t border-[#C2DFF1]/50" />

            {/* Account & Settings */}
            <div className="px-4 py-1">
              <div className="text-[10px] uppercase tracking-wider axon-menu-label">Account & Settings</div>
            </div>

            <button
              onClick={handleProfileClick}
              className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
            >
              <Settings size={16} className="text-gray-400" />
              <span className="header-widget-bold">Profile Settings</span>
            </button>

            <button
              onClick={onSiteInfo}
              className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
            >
              <Info size={16} className="text-gray-400" />
              <span className="header-widget-bold">Site Information</span>
            </button>

            <button
              onClick={() => {
                onToggleMenu();
                navigate(site ? `/guide/${site}` : '/guide');
              }}
              className="dropdown-item flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-gray-700 hover:text-[#1C4D88] hover:bg-[#F2F8FC]"
            >
              <BookOpen size={16} className="text-gray-400" />
              <span className="header-widget-bold">Platform Guide</span>
            </button>
            <div className="px-2 py-1">
              <ThemeToggle variant="menu-item" />
            </div>

            <div className="my-1 mx-4 border-t border-[#C2DFF1]/50" />

            <button
              onClick={onLogout}
              className="dropdown-item logout flex items-center gap-2 w-full text-left px-4 py-2 text-sm text-red-600 font-bold hover:bg-red-50"
              style={{ fontWeight: 700 }}
            >
              <LogOut size={16} className="text-red-400" />
              <span className="header-widget-bold">Logout</span>
            </button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default UserMenu;
